import { useCallback, useEffect, useState } from "react";
import { Sparkles, TriangleAlert } from "lucide-react";
import { fetchFacetBenchmarks } from "../../lib/api";
import Stat from "./Stat";

const Bar = ({ value }) => (
  <div className="mt-1.5 h-[2px] w-full bg-[var(--line)]">
    <div className="h-full bg-zinc-400" style={{ width: `${Math.round(value * 100)}%` }} />
  </div>
);

/** Cross-subject facet recurrence: where the cohort says the fixed catalog is too coarse. */
export const FacetTrendsPanel = () => {
  const [d, setD] = useState(null);

  const load = useCallback(() => fetchFacetBenchmarks().then(setD).catch(() => {}), []);
  useEffect(() => {
    load();
  }, [load]);

  const clusters = d?.clusters || [];

  return (
    <div data-testid="admin-facet-trends-panel" className="space-y-10">
      <div className="grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-4">
        <Stat
          testid="facet-trends-pool"
          k="Pool"
          v={d?.pool_size ?? 0}
          sub={`engagements with ${d?.min_turns ?? 4}+ turns`}
        />
        <Stat k="Distinct findings" v={clusters.length} sub="after folding wordings together" />
        <Stat
          testid="facet-trends-candidates"
          k="Catalog candidates"
          v={d?.taxonomy_candidates?.length ?? 0}
          sub="3+ subjects, strongly felt"
        />
        <Stat
          k="Coarse parents"
          v={d?.coarse_parents?.length ?? 0}
          sub="cap fills up too often"
        />
      </div>

      {d?.thin_pool && (
        <p className="flex items-start gap-2 border border-[#8a6a1f] bg-[rgba(138,106,31,0.06)] px-4 py-3 font-data text-[11px] leading-relaxed text-[#e0b55c]">
          <TriangleAlert size={12} strokeWidth={1.8} className="mt-0.5 shrink-0" />
          Thin pool — {d.pool_size} engagement{d.pool_size === 1 ? "" : "s"} qualify. Recurrence
          here is suggestive, not evidence. Read it again past five.
        </p>
      )}

      {(d?.taxonomy_candidates || []).length > 0 && (
        <section>
          <p className="label flex items-center gap-2">
            <Sparkles size={11} strokeWidth={1.7} /> The catalog is probably missing these
          </p>
          <p className="mt-2 max-w-2xl text-xs leading-relaxed text-zinc-500">
            Named independently by three or more subjects and felt strongly. Each of these is a
            candidate to be promoted into the fixed {d?.categories_total ?? 46} — at which point it
            becomes comparable across everyone, not just discovered per person.
          </p>
          <ul className="mt-4">
            {d.taxonomy_candidates.map((c) => (
              <li
                key={c.key}
                data-testid={`facet-candidate-${c.key}`}
                className="flex flex-wrap items-baseline justify-between gap-3 border-b border-[var(--line)] py-3"
              >
                <span className="text-sm text-zinc-100">{c.label}</span>
                <span className="font-data text-[10px] text-zinc-500">
                  under T{c.tier} {c.parent_name} · {c.subjects} subjects · mean salience{" "}
                  {c.mean_salience} · {c.valence}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <p className="label">Every recurring finding, most shared first</p>
        <ul className="mt-4">
          {clusters.map((c) => (
            <li
              key={c.key}
              data-testid={`facet-cluster-${c.key}`}
              className="grid gap-3 border-b border-[var(--line)] py-4 sm:grid-cols-[1fr_auto]"
            >
              <div className="min-w-0">
                <p className="text-sm text-zinc-100">{c.label}</p>
                <p className="mt-1 font-data text-[10px] text-zinc-600">
                  T{c.tier} · {c.parent_name}
                  {c.variants.filter((v) => v !== c.label).length > 0 &&
                    ` · also said as: ${c.variants.filter((v) => v !== c.label).join(" · ")}`}
                </p>
                <Bar value={c.prevalence} />
              </div>
              <div className="font-data text-[10px] tabular-nums text-zinc-500 sm:text-right">
                <span className="text-zinc-200">
                  {c.subjects} of {d?.pool_size ?? 0} subjects
                </span>
                <br />
                salience {c.mean_salience} · {c.mentions} mentions · {c.promoted_subjects} standing
              </div>
            </li>
          ))}
          {clusters.length === 0 && (
            <li className="py-4 font-data text-[11px] text-zinc-600">
              No facets recorded yet in any qualifying engagement.
            </li>
          )}
        </ul>
      </section>

      <section>
        <p className="label">Which majors are generating the most facets</p>
        <p className="mt-2 max-w-2xl text-xs leading-relaxed text-zinc-500">
          A parent whose four slots keep filling up is a label doing too much work — the honest
          response is to split it in the catalog rather than let the cap throw findings away.
        </p>
        <ul className="mt-4">
          {(d?.parents || []).map((p) => (
            <li
              key={p.parent}
              data-testid={`facet-parent-stat-${p.parent}`}
              className="flex flex-wrap items-baseline justify-between gap-3 border-b border-[var(--line)] py-3"
            >
              <span className="text-xs text-zinc-200">
                T{p.tier} {p.parent_name}
              </span>
              <span className="font-data text-[10px] text-zinc-500">
                {p.facets} facets across {p.subjects} subjects · {p.mean_facets_per_subject} each ·
                cap reached in {Math.round(p.saturation_rate * 100)}%
              </span>
            </li>
          ))}
          {(d?.parents || []).length === 0 && (
            <li className="py-4 font-data text-[11px] text-zinc-600">Nothing to compare yet.</li>
          )}
        </ul>
      </section>
    </div>
  );
};

export default FacetTrendsPanel;
